import type { AnomalyDetail, WeldSummary } from '../types/weld';

export interface SystemAggregate {
  weldingSystem: string;
  welds: number;
  anomalous: number;
  failed: number;
  totalPasses: number;
  avgScore: number | null;
}

export function aggregateByWeldingSystem(welds: WeldSummary[]): SystemAggregate[] {
  const map = new Map<string, SystemAggregate & { scoreSum: number; scoreCount: number }>();

  welds.forEach((w) => {
    const key = w.welding_system || 'Unknown';
    if (!map.has(key)) {
      map.set(key, {
        weldingSystem: key,
        welds: 0,
        anomalous: 0,
        failed: 0,
        totalPasses: 0,
        avgScore: null,
        scoreSum: 0,
        scoreCount: 0,
      });
    }
    const row = map.get(key)!;
    row.welds += 1;
    if ((w.anomalous_passes || 0) > 0) row.anomalous += 1;
    row.failed += w.failed_passes || 0;
    row.totalPasses += w.total_passes || 0;
    if (w.composite_score != null) {
      row.scoreSum += w.composite_score;
      row.scoreCount += 1;
    }
  });

  return Array.from(map.values())
    .map(({ scoreSum, scoreCount, ...rest }) => ({
      ...rest,
      avgScore: scoreCount > 0 ? scoreSum / scoreCount : null,
    }))
    .sort((a, b) => {
      const rateA = a.welds > 0 ? a.anomalous / a.welds : 0;
      const rateB = b.welds > 0 ? b.anomalous / b.welds : 0;
      return rateB - rateA;
    });
}

export function countByField<T>(items: T[], getter: (item: T) => string): Record<string, number> {
  const counts: Record<string, number> = {};
  items.forEach((item) => {
    const key = getter(item) || 'Unknown';
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}

export function topWeldsByRank(welds: WeldSummary[], limit = 20): WeldSummary[] {
  return [...welds]
    .sort((a, b) => (a.weld_rank ?? 99999) - (b.weld_rank ?? 99999))
    .slice(0, limit);
}

export function filterAnomaliesForJob(
  anomalies: AnomalyDetail[],
  jobNumber: string,
): AnomalyDetail[] {
  if (!jobNumber) return anomalies;
  return anomalies.filter((a) => a.job_number === jobNumber);
}
